import React from "react";
import { useLazyLoadQuery } from "react-relay";

import { graphql } from "relay-runtime";
import { RRule } from "rrule";

import { EventFormContext } from "~/calendar/EventForm/EventFormContext";
import { formatTime, getLocaleCurrency } from "~/calendar/utils/meetingGuidelinesUtils";
import { getRRuleFromRepeatAndInterval } from "~/stream/utils";

import type { Participant } from "../hooks/useParticipantsOptions";
import { getNumOccurrencesFromRRule } from "../utils/meetingGuidelinesUtils";
import MeetingCost from "./MeetingCost";

import type { MeetingCostEditPanelUseCalculateMeetingCostQuery } from "~/__generated__/MeetingCostEditPanelUseCalculateMeetingCostQuery.graphql";

import styles from "./MeetingCostEditPanel.module.css";

interface MeetingCostEditPanelProps {
	attendees: (Partial<Participant> & MeetingCostAttendee)[];
}

type MeetingCostAttendee = { id: string | undefined };

export function convertAnnualizedCostToCostByMinute(annualizedCost: number) {
	// Annualized cost is the cost of a full-time employee (52 weeks, 40 hours a week, 60 minutes an hour)
	return annualizedCost / 52 / 40 / 60;
}

export function useCalculateMeetingCost(
	attendees: MeetingCostAttendee[],
	meetingLengthInMinutes: number,
	numOccurrences: number,
	doNotIncludeViewer?: boolean,
) {
	const query = graphql`
		query MeetingCostEditPanelUseCalculateMeetingCostQuery($userIds: [ID!]!) {
			viewer {
				user {
					annualizedMeetingCost
				}
				account {
					extensionSettings {
						meetingCostEnabled
						meetingCostUnit
						meetingCostAvgSalary
					}
				}
				usersById(userIds: $userIds) {
					annualizedMeetingCost
				}
			}
		}
	`;

	const userIds = attendees.map(attendee => attendee?.id).filter(id => id !== undefined) as string[];

	const {
		viewer: { usersById, account, user: currentUser },
	} = useLazyLoadQuery<MeetingCostEditPanelUseCalculateMeetingCostQuery>(query, {
		userIds,
	});

	const meetingCostEnabled = account?.extensionSettings?.meetingCostEnabled;
	const meetingCostUnit = account?.extensionSettings?.meetingCostUnit;

	const totalMinutes = attendees.length * meetingLengthInMinutes;
	let totalCost = 0;

	const allUsers = doNotIncludeViewer ? usersById : [currentUser, ...(usersById ?? [])];

	if (!allUsers) {
		return {
			meetingCostOccurrence: formatTime(totalMinutes),
			meetingCostSeries: formatTime(totalMinutes * numOccurrences),
			isMonetary: false,
		};
	}

	for (const user of allUsers) {
		const costByMinute = convertAnnualizedCostToCostByMinute(user.annualizedMeetingCost ?? 0);
		totalCost += costByMinute * meetingLengthInMinutes;
	}

	// Add the cost of the unknown attendees
	const costPerMinuteOfUnknownAttendee = convertAnnualizedCostToCostByMinute(
		account?.extensionSettings?.meetingCostAvgSalary ?? 0,
	);

	totalCost +=
		(costPerMinuteOfUnknownAttendee ?? 0) * (attendees.length - (usersById?.length ?? 0)) * meetingLengthInMinutes;

	if (meetingCostEnabled && meetingCostUnit === "CURRENCY") {
		return {
			meetingCostOccurrence: getLocaleCurrency(totalCost),
			meetingCostSeries: getLocaleCurrency(totalCost * numOccurrences),
			isMonetary: true,
		};
	}

	return {
		meetingCostOccurrence: formatTime(totalMinutes),
		meetingCostSeries: formatTime(totalMinutes * numOccurrences),
		isMonetary: false,
	};
}

export default function MeetingCostEditPanel({ attendees }: MeetingCostEditPanelProps) {
	const { timeState, repeatState, customRrule } = React.useContext(EventFormContext);

	let filteredAttendees = attendees.filter(attendee => attendee?.responseStatus !== "declined");

	const attendeeUsers = filteredAttendees.map(attendee => ({
		id: attendee.user?.id,
	}));

	const meetingLengthInMinutes = (timeState.end.getTime() - timeState.start.getTime()) / 1000 / 60;

	let occurrencesInfo = { numOccurrences: 1, isInfinite: false };
	if (repeatState !== "NEVER") {
		const rRule = getRRuleFromRepeatAndInterval(repeatState, timeState.start, customRrule ?? undefined);
		const rRuleString = RRule.optionsToString(rRule?.options ?? {});
		occurrencesInfo = getNumOccurrencesFromRRule(rRuleString, timeState.start);
	}

	const { numOccurrences, isInfinite } = occurrencesInfo;

	const { meetingCostOccurrence, meetingCostSeries, isMonetary } = useCalculateMeetingCost(
		attendeeUsers,
		meetingLengthInMinutes,
		numOccurrences,
	);

	if (timeState.isAllDay || filteredAttendees.length === 0) return null;

	return (
		<MeetingCost
			wrapperStyles={styles}
			meetingCostOccurrence={meetingCostOccurrence}
			meetingCostSeries={meetingCostSeries}
			numOccurrences={numOccurrences}
			isMonetary={isMonetary}
			isInfinite={isInfinite}
		/>
	);
}
